import cx from 'classnames';
import {
  forwardRef,
  ComponentProps,
  ReactNode,
  useRef,
  useState,
  ChangeEvent,
  FocusEvent,
  useEffect,
  useMemo,
} from 'react';

import { useDisableFocus } from '../../../../providers/disable-focus';
import Button from '../../../button';
import BPImage from '../../image';
import Cheverlon from '../../../icons/cheverlon';
import FilterIcon from '../../../icons/filter';
import { ErrorLine } from '../errors';

import classes from './text.module.css';
import IconCloseRounded from '../../../icons/close-rounded';
import { useFormContext, useWatch } from 'react-hook-form';
import EyeClose from '../../../icons/eye-close';
import EyeOpen from '../../../icons/eye-open';

type TextInputProps = {
  as?: 'input' | 'textarea';
  hideLabel?: boolean;
  label?: string;
  labelBehaviour?: 'default' | 'animated';
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  variant?: 'filter' | 'select' | 'search' | 'default' | 'eyeOpen' | 'eyeClose';
  border?: boolean;
  error_messages?: Array<string>;
  inputExtraCss?: string;
  /**
   * Classes extra na label, aplicadas por último. Existe porque a label junta
   * `text-xs` (do `getVariantCSS`) e `text-sm` no mesmo `cx` — quem quiser um
   * tamanho concreto tem de o impor com `!`, ex: `labelExtraCss="!text-sm"`.
   */
  labelExtraCss?: string;
  /**
   * Um nó a seguir ao texto da label, dentro da mesma `<label>`. Para marcas
   * que não devem ter o peso nem o tamanho do rótulo — "(Opcional)" é o caso.
   *
   * Fica à parte do `label` de propósito: esse é `string` porque alimenta
   * também o `aria-label` e o `placeholder` de quem esconde a label, e um nó
   * em qualquer um dos dois dava lixo.
   */
  labelSuffix?: ReactNode;
  clearButton?: boolean;
  onClear?: () => void;
  focusBg?: boolean;
  showExtraIcon?: boolean;
  disabled?: boolean;
  scrollToTop?: boolean;
  backButtonOnFocus?: boolean;
  fullWidth?: boolean;
  inputPattern?: string;
  idToFocusOnMax?: string;
  idToFocusOnMin?: string;
  inputmaxLength?: number;
  children?: ReactNode;
  forceClearButton?: boolean;
  bgColor?: any;
  errorBehaviour?: 'default' | 'absolute';
  reserveErrorSpace?: boolean;
  extraCSSMain?: string;
} & ComponentProps<'input'>;

const getIcon = (variant: TextInputProps['variant'], styles?: string) => {
  switch (variant) {
    case 'search':
      return <FilterIcon className={styles} />;
    case 'filter':
      return <FilterIcon className={styles} />;
    case 'select':
      return <Cheverlon className={styles} />;
    case 'eyeOpen':
      return <EyeOpen className={styles} />;
    case 'eyeClose':
      return <EyeClose className={styles} />;

    default:
      break;
  }
};

export const getVariantCSS = (
  variant: TextInputProps['labelBehaviour'],
  inputFocused?: any
) => {
  switch (variant) {
    case 'animated':
      /*To be implemented 
      if (backButtonOnFocus && inputFocused) {
        return cx(
          classes.animatedLabel,
          'absolute',
          '!left-[48px]',
          'text-neutral-80'
        );
      }*/
      return cx(classes.animatedLabel);
    case 'default':
    default:
      return cx('absolute', 'text-xs', 'font-bold', 'top-0 max-lg:top-1');
  }
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      as: As = 'input',
      hideLabel = false,
      labelBehaviour = 'default',
      id,
      label,
      leftIcon,
      rightIcon,
      variant = 'default',
      border = false,
      error_messages = [],
      onChange: onChangeCallback,
      onFocus: onFocusCallback,
      onBlur: onBlurCallback,
      placeholder: originalPlaceholder,
      inputExtraCss,
      labelExtraCss,
      labelSuffix,
      clearButton,
      onClear,
      focusBg = false,
      showExtraIcon = false,
      disabled = false,
      scrollToTop = false,
      backButtonOnFocus = false,
      fullWidth = false,
      inputPattern = undefined,
      inputmaxLength = undefined,
      idToFocusOnMin = undefined,
      idToFocusOnMax = undefined,
      forceClearButton = false,
      bgColor,
      errorBehaviour = 'default',
      reserveErrorSpace = false,
      children,
      extraCSSMain,
      ...props
    },
    ref
  ) => {
    const formContext = useFormContext();
    const disableFocus = useDisableFocus();
    const innerRef = useRef<HTMLInputElement>(null);
    const labelRef = useRef<HTMLLabelElement>(null);
    const [showErrors, setShowErrors] = useState<boolean>(
      !!error_messages.length
    );
    const [isInputWithText, setIsInputWithText] = useState<boolean>(
      !!(
        (formContext &&
          props &&
          props.name &&
          formContext.getValues(props.name)) || props.value
      )
    );
    const haveText = (formContext &&
      props &&
      props.name) ? formContext.watch(props.name) : props.value;

    useEffect(() => {
      if (error_messages.length > 0) {
        setShowErrors(true);
      }
    }, [error_messages]);

    useEffect(() => {
      if (!!(
        formContext &&
        props &&
        props.name &&
        formContext.getValues(props.name)
      )) {
        setIsInputWithText(true);
      } else {
        if (props.value) {
          setIsInputWithText(true);
        } else {
          setIsInputWithText(false);
        }
      }
    }, [formContext, props, props.name]);

    const onClearButton = () => {
      if (
        (formContext &&
          props &&
          props.name &&
          formContext.getValues(props.name)) || props.value
      ) {
        if (formContext &&
          props &&
          props.name) {
          formContext.setValue(props.name, undefined);
        }
        setIsInputWithText(false);
      }

      if (onClear) {
        onClear();
      }
    };

    const customOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsInputWithText(true);
      if (error_messages.length) setShowErrors(false);

      if (onChangeCallback) {
        onChangeCallback(e);
      }
    };

    const customOnFocus = (e: FocusEvent<HTMLInputElement>) => {
      if (onFocusCallback) {
        onFocusCallback(e);
      }
    };

    const customOnBlur = (e: FocusEvent<HTMLInputElement>) => {
      if (e.target.value === '') {
        setIsInputWithText(false);
      }

      if (onBlurCallback) {
        onBlurCallback(e);
      }
    };

    return (
      <div
        className={cx(
          'relative',
          'w-full items-start',
          reserveErrorSpace ? 'min-h-[72px]' : 'min-h-12',
          'flex flex-col gap-1',
          bgColor
        )}
      >
        <div
          className={cx(
            'relative h-12',
            (((isInputWithText || props.value) && !disabled && !props.readOnly) || ((isInputWithText || props.value) && forceClearButton && !props.readOnly)) ? 'items-center' : 'items-end',
            {
              'w-full grid grid-cols-[1fr_48px]': clearButton && !showExtraIcon,
              'w-full grid grid-cols-[1fr_48px_48px]':
                clearButton && showExtraIcon,
            },
            {
              'w-full': fullWidth,
            }, ...(border
              ? [
                'border-b',
                showErrors && error_messages.length
                  ? 'border-red-100'
                  : disabled
                    ? 'border-neutral-50 bg-neutral-5'
                    : 'border-neutral-80 group-focus:border-brand-100 group-focus-within:border-brand-100',
              ]
              : []),
            extraCSSMain,
          )}
        >
          <div
            className={cx(
              (((isInputWithText || props.value) && !disabled && !props.readOnly) || ((isInputWithText || props.value) && forceClearButton && !props.readOnly)) ? (props.type === 'date' ? 'h-full flex flex-col justify-end' : '') : labelBehaviour === 'animated' ? 'flex h-full items-center pl-4 justify-start' : 'flex h-full items-end pl-4 mb-2 justify-start',
              clearButton ? "truncate" : "px-4 max-lg:truncate"
            )}
          >
            {label && !hideLabel && (
              <label
                ref={labelRef}
                htmlFor={id}
                className={cx(
                  {
                    'group-focus-within:text-brand-100 block left-4 text-neutral-80 z-[1]': labelBehaviour !== 'animated',
                    'block text-neutral-80 z-[1] group-focus-within:text-brand-100': labelBehaviour === 'animated' && haveText,
                    'text-white absolute': labelBehaviour === 'animated' && !haveText,
                    '!text-red-100': labelBehaviour === 'animated' && haveText && showErrors && error_messages.length,
                  },
                  {
                    [classes.focus]: isInputWithText,
                  },
                  getVariantCSS(labelBehaviour),
                  {
                    'group-focus:text-neutral-50 bg-neutral-5': disabled,
                  },
                  'text-sm',
                  labelExtraCss
                )}
                onClick={() => innerRef.current?.focus()}
              >
                {label}
                {labelSuffix}
              </label>
            )}
            <div
              className={cx(
                { flex: !clearButton },
                { 'group-focus-within:bg-brand-10': focusBg },
                props.type !== 'date' ? 'grow' : 'max-lg:!absolute max-lg:w-[calc(100%-16px)] max-lg:grow',
                classes.container
              )}
              onClick={() => {
                if (disabled) return;
                if (innerRef.current) {
                  innerRef.current.focus();
                  if (props.type === 'date' && 'showPicker' in innerRef.current) {
                    try {
                      innerRef.current.showPicker();
                    } catch (e) {
                      // Silently catch in cases where the browser blocks programmatic picker
                    }
                  }
                }
              }}
            >
              <As
                className={cx(
                  'flex',
                  'justify-start',
                  'text-left',
                  'text-base',
                  'leading-5',
                  'w-full',
                  'border-none',
                  'outline-none',
                  'bg-transparent',
                  'disabled:bg-neutral-5',
                  'rounded-none',
                  {
                    'placeholder-red-100': showErrors && error_messages.length,
                  },
                  {
                    'placeholder-neutral-80 group-focus-within:placeholder-brand-100':
                      !showErrors || !error_messages.length,
                    '!text-neutral-50 !placeholder-neutral-50 !bg-neutral-5': disabled,
                  },
                  inputExtraCss,
                  classes.inputField,
                  (isInputWithText || props.value) ? 'has-value' : '',
                  'h-full',
                  (!isInputWithText && !props.value && labelBehaviour === 'animated') ?
                    'items-center justify-center' :
                    labelBehaviour === 'animated' ?
                      disabled ? (forceClearButton ? 'pt-5 pl-4 max-lg:px-4' : 'pt-5') : (props.type === 'date' ? 'pt-5 pl-4 max-lg:px-4' : 'pt-5 -mt-1 pl-4 max-lg:px-4') :
                      !isInputWithText && !props.value ? 'pt-5 max-lg:pt-8' : (props.type === 'date' ? 'pt-5 pl-4 max-lg:px-4' : 'pt-5 -mt-1 pl-4 max-lg:px-4'),
                  props.type === 'date' && '!pl-0'
                )}
                name={id}
                id={id}
                tabIndex={disableFocus ? -1 : 0}
                ref={(node: any) => {
                  (innerRef as React.MutableRefObject<any>).current = node;
                  if (typeof ref === 'function') {
                    ref(node);
                  } else if (ref) {
                    (ref as React.MutableRefObject<any>).current = node;
                  }
                }}
                aria-label={label}
                disabled={disabled}
                {...props}
                style={props.type === 'date' ? { ...(props.style || {}), '--bg-calendar': `url('${process.env.PUBLIC_PREFIX}/svg/icons/calendar.svg')` } as React.CSSProperties : props.style}
                type={props.type}
                onChange={customOnChange}
                onFocus={customOnFocus}
                onBlur={customOnBlur}
                placeholder={
                  props.type === 'date' && !originalPlaceholder ? 'dd/mm/yyyy' : (originalPlaceholder || (hideLabel && label) || undefined)
                }
                maxLength={inputmaxLength}
                pattern={inputPattern}
                onInput={(e) => {
                  const ismaxLength =
                    e.currentTarget.value.length.toString() ===
                    e.currentTarget.getAttribute('maxLength');
                  if (ismaxLength && idToFocusOnMax) {
                    document.getElementById(idToFocusOnMax)?.focus();
                  }
                  const isMinLength = e.currentTarget.value.length === 0;
                  if (isMinLength && idToFocusOnMin) {
                    document.getElementById(idToFocusOnMin)?.focus();
                  }
                }}
              />
              {props.type === 'date' && !isInputWithText && !props.value && (
                <div className={cx("absolute bottom-1 pointer-events-none text-neutral-80 text-base leading-5 lg:hidden", "left-4 max-lg:left-0", inputExtraCss)}>
                  {originalPlaceholder || 'dd/mm/yyyy'}
                </div>
              )}
              {props.type === 'date' && (
                <div className="absolute right-4 bottom-2 pointer-events-none lg:hidden flex items-center justify-center h-4 w-4">
                  <BPImage
                    src={`${process.env.PUBLIC_PREFIX}/svg/icons/calendar.svg`}
                    width={16}
                    height={16}
                    className="max-w-max object-contain pointer-events-none"
                    alt="Date icon"
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            {((isInputWithText || props.value) && !disabled && !props.readOnly) || ((isInputWithText || props.value) && forceClearButton && !props.readOnly) ? (
              clearButton && (
                <Button
                  type="button"
                  onClick={onClearButton}
                  className={cx('')}
                >
                  <IconCloseRounded
                    className={cx(
                      'h-4',
                      'w-4',
                      'group-focus-within:text-brand-100',
                      {
                        'text-neutral-50 bg-neutral-5': disabled && !forceClearButton,
                        'bg-neutral-5': disabled && forceClearButton
                      }
                    )}
                  ></IconCloseRounded>
                </Button>
              )
            ) : <>{clearButton && (
              <div className={cx('h-12 w-12')}></div>
            )}</>}
          </div>
          {showExtraIcon && <>{children ? children :
            <div
              className={cx('flex justify-self-center self-center', {
                'text-neutral-50 bg-neutral-5': disabled,
              })}
              onClick={() => {
                innerRef.current?.focus();
              }}
            >
              {getIcon(
                variant,
                cx('h-4', 'w-4', 'group-focus-within:text-brand-100')
              )}
            </div>
          }</>}
        </div>
        {showErrors && error_messages && (
          <div className={cx('px-4', {
            'pb-2': errorBehaviour === 'default',
            'absolute top-full left-0 w-full z-10': errorBehaviour === 'absolute',
          })}>
            {error_messages.map((m, k) => (
              <ErrorLine key={`${m}-${k}`} message={m} />
            ))}
          </div>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
